import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AnyObject } from "../interfaces/anyObject";

interface ComplianceState {
  complianceTypes: string[];
  consents: AnyObject;
  error: Record<string, AnyObject> | undefined;
  externalContentEnabled: boolean;
  loading: boolean;
  show: boolean;
}

const initialState: ComplianceState = {
  complianceTypes: [],
  consents: {},
  error: {},
  externalContentEnabled: false,
  loading: false,
  show: false,
};

export const getComplianceTypes = createAsyncThunk<
  // Return type of the payload creator
  string[],
  // First argument to the payload creator
  void,
  // Types for ThunkAPI
  {
    rejectValue: AnyObject;
  }
>(
  "complianceConsent/getComplianceTypes",
  async (args, { fulfillWithValue, rejectWithValue }) => {
    const { ppms } = window;

    if (ppms) {
      return new Promise((resolve) => {
        ppms.cm.api(
          "getComplianceTypes",
          (response) => {
            resolve(fulfillWithValue(response));
          },
          (error) => {
            resolve(rejectWithValue(error));
          },
        );
      });
    }

    return rejectWithValue({ error: "PPMS undefined" });
  },
);

export const getComplianceSettings = createAsyncThunk<
  AnyObject,
  void,
  {
    rejectValue: AnyObject;
  }
>(
  "complianceConsent/getComplianceSettings",
  async (args, { fulfillWithValue, rejectWithValue }) => {
    const { ppms } = window;

    if (ppms) {
      return new Promise((resolve) => {
        ppms.cm.api(
          "getComplianceSettings",
          (response) => {
            resolve(fulfillWithValue(response));
          },
          (error) => {
            resolve(rejectWithValue(error));
          },
        );
      });
    }

    return rejectWithValue({ error: "PPMS undefined" });
  },
);

export const setComplianceSettings = createAsyncThunk<
  PiwikProCmApiSetterResponses["setComplianceSettings"][0],
  PiwikProConsentTypes & { consents: AnyObject },
  {
    rejectValue: AnyObject;
  }
>(
  "complianceConsent/setComplianceSettings",
  async (args, { fulfillWithValue, rejectWithValue }) => {
    const { ppms } = window;

    if (ppms) {
      return new Promise((resolve) => {
        ppms.cm.api(
          "setComplianceSettings",
          args,
          (response) => {
            resolve(fulfillWithValue(response));
          },
          (error) => {
            resolve(rejectWithValue(error));
          },
        );
      });
    }

    return rejectWithValue({ error: "PPMS undefined" });
  },
);

const ComplianceConsentSlice = createSlice({
  extraReducers: (builder) => {
    // getComplianceTypes
    builder.addCase(getComplianceTypes.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getComplianceTypes.fulfilled, (state, { payload }: PayloadAction<string[]>) => {
      state.loading = false;
      state.complianceTypes = payload;
    });
    builder.addCase(getComplianceTypes.rejected, (state, { payload }: PayloadAction<AnyObject | undefined>) => {
      state.error = payload;
      state.loading = false;
    });

    // getComplianceSettings
    builder.addCase(getComplianceSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getComplianceSettings.fulfilled, (state, { payload }: PayloadAction<AnyObject>) => {
      state.loading = false;
      state.externalContentEnabled = payload.consents && payload.consents.custom_consent && typeof payload.consents.custom_consent.status !== "undefined"
        ? payload.consents.custom_consent.status === 1
        : false;
      state.consents = payload.consents;
    });
    builder.addCase(getComplianceSettings.rejected, (state, { payload }: PayloadAction<AnyObject | undefined>) => {
      state.error = payload;
      state.loading = false;
    });

    // setComplianceSettings
    builder.addCase(setComplianceSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(setComplianceSettings.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(setComplianceSettings.rejected, (state, { payload }: PayloadAction<AnyObject | undefined>) => {
      state.error = payload;
      state.loading = false;
    });
  },
  initialState,
  name: "complianceConsent",
  reducers: {
    setShowComplianceSettings: (state, { payload }: PayloadAction<boolean>) => {
      state.show = payload;
    },
  },
});

export const {
  setShowComplianceSettings,
} = ComplianceConsentSlice.actions;

export default ComplianceConsentSlice.reducer;
